import React, { useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useSearchContext, PUBLIC_CATEGORIES, ALL_CATEGORIES, ALL_AREAS } from '../../context/searchContext';
import {
    label_category_sweets, label_category_cafe, label_category_bakery,
    label_area_chuo, label_area_higashinada, label_area_nada, label_area_hyogo,
    label_area_kita, label_area_nagata, label_area_suma, label_area_tarumi, label_area_nishi,
    label_recommend_1, label_recommend_2, label_recommend_3,
    label_eatin, label_takeout, label_eatin_takeout,
} from "../../assets/label/label";
import { convertRecommendToNumber, convertEatinTakeoutToNumber } from "../common/text";

const GENRE_DEFS = [
    { label: label_category_sweets, icon: '🍰', color: '#c97bab' },
    { label: label_category_cafe, icon: '☕', color: '#8a6e5e' },
    { label: label_category_bakery, icon: '🍞', color: '#b87040' },
];

const AREA_LIST = [
    label_area_chuo, label_area_higashinada, label_area_nada,
    label_area_hyogo, label_area_kita, label_area_nagata,
    label_area_suma, label_area_tarumi, label_area_nishi,
];

const RATE_DEFS = [
    { val: 3, emo: '◎◎◎', label: label_recommend_3 },
    { val: 2, emo: '◎◎', label: label_recommend_2 },
    { val: 1, emo: '◎', label: label_recommend_1 },
];

const EAT_DEFS = [
    { val: convertEatinTakeoutToNumber(label_eatin), label: label_eatin, icon: '🪑' },
    { val: convertEatinTakeoutToNumber(label_takeout), label: label_takeout, icon: '🛍' },
    { val: convertEatinTakeoutToNumber(label_eatin_takeout), label: label_eatin_takeout, icon: '🪑🛍' },
];

const ALL_RECOMMEND_STR = [
    convertRecommendToNumber(label_recommend_1),
    convertRecommendToNumber(label_recommend_2),
    convertRecommendToNumber(label_recommend_3),
].join('|');

const ALL_EAT_STR = [
    convertEatinTakeoutToNumber(label_eatin),
    convertEatinTakeoutToNumber(label_takeout),
    convertEatinTakeoutToNumber(label_eatin_takeout),
].join('|');

const INITIAL_DRAFT = { q: '', cats: [], areas: [], rates: [], eat: [], sort: 'updated' };

function SecTitle({ children, n }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <Box sx={{ width: 4, height: 16, bgcolor: 'var(--accent)', borderRadius: '2px', flexShrink: 0 }} />
            <Typography sx={{ fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 700, color: 'var(--ink)' }}>
                {children}
            </Typography>
            {n > 0 && (
                <Typography sx={{ fontSize: 11, color: 'var(--accent)', fontWeight: 700 }}>
                    · {n}件
                </Typography>
            )}
        </Box>
    );
}

function Toggle({ active, onClick, children, icon, color }) {
    return (
        <Box
            component="button"
            onClick={onClick}
            sx={{
                display: 'inline-flex', alignItems: 'center', gap: 0.75,
                px: '14px', py: '8px',
                border: '1.5px solid',
                borderColor: active ? (color || 'var(--accent)') : 'var(--line)',
                bgcolor: active ? (color || 'var(--accent)') : 'var(--paper)',
                color: active ? 'white' : 'var(--ink)',
                borderRadius: '999px',
                fontSize: 13, fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'inherit',
                whiteSpace: 'nowrap',
                transition: 'all 0.15s',
                '&:hover': { opacity: 0.85 },
            }}
        >
            {icon && <span>{icon}</span>}
            {children}
        </Box>
    );
}

function SearchScreen({ open, onClose }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const {
        searchKeyword, searchCategory, searchArea,
        searchRecommend, searchEatinTakeout, searchSort, showAll,
        setSearchKeyword, setSearchCategory, setSearchArea,
        setSearchRecommend, setSearchEatinTakeout, setSearchSort, setCurrentPage,
    } = useSearchContext();

    const [draft, setDraft] = useState(INITIAL_DRAFT);

    useEffect(() => {
        if (!open) return;
        const isDefaultCat = searchCategory === PUBLIC_CATEGORIES || searchCategory === ALL_CATEGORIES;
        const isDefaultArea = searchArea === ALL_AREAS;
        const isDefaultRate = searchRecommend === ALL_RECOMMEND_STR;
        const isDefaultEat = searchEatinTakeout === ALL_EAT_STR;

        setDraft({
            q: searchKeyword.replace(/\|/g, ' '),
            cats: isDefaultCat ? [] : searchCategory.split('|').filter(c =>
                GENRE_DEFS.some(g => g.label === c)
            ),
            areas: isDefaultArea ? [] : searchArea.split('|').filter(Boolean),
            rates: isDefaultRate ? [] : searchRecommend.split('|').map(Number).filter(Boolean),
            eat: isDefaultEat ? [] : searchEatinTakeout.split('|').map(Number).filter(Boolean),
            sort: searchSort || 'recommend',
        });
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    const toggle = (key, val) => {
        setDraft(d => {
            const arr = d[key];
            return { ...d, [key]: arr.includes(val) ? arr.filter(x => x !== val) : [...arr, val] };
        });
    };

    const handleApply = () => {
        const defaultCats = showAll ? ALL_CATEGORIES : PUBLIC_CATEGORIES;
        setSearchKeyword(draft.q.replace(/ /g, '|').replace(/\s+/g, '|'));
        setSearchCategory(draft.cats.length ? draft.cats.join('|') : defaultCats);
        setSearchArea(draft.areas.length ? draft.areas.join('|') : ALL_AREAS);
        setSearchRecommend(draft.rates.length ? draft.rates.join('|') : ALL_RECOMMEND_STR);
        setSearchEatinTakeout(draft.eat.length ? draft.eat.join('|') : ALL_EAT_STR);
        setSearchSort(draft.sort || 'recommend');
        setCurrentPage(1);
        onClose();
    };

    const count = draft.cats.length + draft.areas.length + draft.rates.length + draft.eat.length + (draft.q ? 1 : 0);

    if (!open) return null;

    return (
        <Box
            onClick={onClose}
            sx={{
                position: 'fixed', inset: 0, zIndex: 200,
                bgcolor: 'rgba(26,20,10,0.55)',
                display: 'flex',
                alignItems: isMobile ? 'flex-end' : 'center',
                justifyContent: 'center',
                p: isMobile ? 0 : '40px',
            }}
        >
            <Box
                onClick={e => e.stopPropagation()}
                sx={{
                    width: isMobile ? '100%' : '560px',
                    maxWidth: isMobile ? '100%' : '560px',
                    height: isMobile ? '92%' : 'auto',
                    maxHeight: isMobile ? '92%' : '88vh',
                    bgcolor: 'var(--paper)',
                    borderRadius: isMobile ? '20px 20px 0 0' : '20px',
                    boxShadow: '0 24px 60px rgba(0,0,0,0.25)',
                    display: 'flex', flexDirection: 'column',
                    overflow: 'hidden',
                }}
            >
                {/* ヘッダー */}
                <Box sx={{
                    px: isMobile ? 2 : 3, py: isMobile ? 1.75 : 2.25,
                    borderBottom: '1px solid var(--line)',
                    display: 'flex', alignItems: 'center', gap: 1.5,
                    flexShrink: 0,
                }}>
                    <Typography sx={{ fontFamily: 'var(--font-display)', fontSize: isMobile ? 16 : 18, fontWeight: 800, color: 'var(--ink)' }}>
                        絞り込み・並べ替え
                    </Typography>
                    {count > 0 && (
                        <Box sx={{
                            bgcolor: 'var(--accent)', color: 'white',
                            fontSize: 11, fontWeight: 700,
                            px: 1, py: '2px', borderRadius: '999px',
                        }}>
                            {count}
                        </Box>
                    )}
                    <Box
                        component="button"
                        onClick={onClose}
                        sx={{
                            ml: 'auto', border: 'none', bgcolor: 'transparent',
                            fontSize: 20, cursor: 'pointer', color: 'var(--muted)',
                            p: '4px', fontFamily: 'inherit', lineHeight: 1,
                            '&:hover': { color: 'var(--ink)' },
                        }}
                    >
                        ✕
                    </Box>
                </Box>

                {/* スクロール領域 */}
                <Box sx={{ flex: 1, overflowY: 'auto', px: isMobile ? 2 : 3, py: isMobile ? 2.25 : 2.75 }}>

                    {/* フリーワード */}
                    <Box component="section" sx={{ mb: 3 }}>
                        <SecTitle n={draft.q ? 1 : 0}>フリーワード</SecTitle>
                        <Box sx={{
                            display: 'flex', alignItems: 'center', gap: 1,
                            bgcolor: 'var(--surface)', borderRadius: '12px',
                            px: 1.75, py: 1.25,
                        }}>
                            <span style={{ fontSize: 14, color: 'var(--muted)' }}>🔍</span>
                            <Box
                                component="input"
                                value={draft.q}
                                onChange={e => setDraft(d => ({ ...d, q: e.target.value }))}
                                placeholder="店名・料理・キーワード…"
                                sx={{
                                    flex: 1, border: 'none', bgcolor: 'transparent',
                                    fontSize: 14, fontFamily: 'inherit', outline: 'none',
                                    color: 'var(--ink)',
                                    '&::placeholder': { color: 'var(--muted)' },
                                }}
                            />
                            {draft.q && (
                                <Box
                                    component="span"
                                    onClick={() => setDraft(d => ({ ...d, q: '' }))}
                                    sx={{ fontSize: 12, color: 'var(--muted)', cursor: 'pointer', userSelect: 'none' }}
                                >
                                    ✕
                                </Box>
                            )}
                        </Box>
                    </Box>

                    {/* ジャンル */}
                    <Box component="section" sx={{ mb: 3 }}>
                        <SecTitle n={draft.cats.length}>ジャンル</SecTitle>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {GENRE_DEFS.map(g => (
                                <Toggle
                                    key={g.label}
                                    active={draft.cats.includes(g.label)}
                                    onClick={() => toggle('cats', g.label)}
                                    icon={g.icon}
                                    color={g.color}
                                >
                                    {g.label}
                                </Toggle>
                            ))}
                        </Box>
                    </Box>

                    {/* エリア */}
                    <Box component="section" sx={{ mb: 3 }}>
                        <SecTitle n={draft.areas.length}>エリア</SecTitle>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
                            {AREA_LIST.map(a => {
                                const active = draft.areas.includes(a);
                                return (
                                    <Box
                                        key={a}
                                        component="button"
                                        onClick={() => toggle('areas', a)}
                                        sx={{
                                            p: '10px 8px',
                                            border: '1.5px solid',
                                            borderColor: active ? 'var(--accent)' : 'var(--line)',
                                            bgcolor: active ? 'var(--hero)' : 'var(--paper)',
                                            borderRadius: '12px',
                                            cursor: 'pointer',
                                            fontFamily: 'inherit',
                                            textAlign: 'left',
                                            transition: 'all 0.15s',
                                        }}
                                    >
                                        <Typography sx={{
                                            fontFamily: 'var(--font-display)',
                                            fontSize: 13, fontWeight: 700, color: 'var(--ink)',
                                        }}>
                                            {a}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>

                    {/* おすすめ度 */}
                    <Box component="section" sx={{ mb: 3 }}>
                        <SecTitle n={draft.rates.length}>おすすめ度</SecTitle>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {RATE_DEFS.map(r => {
                                const active = draft.rates.includes(r.val);
                                return (
                                    <Box
                                        key={r.val}
                                        component="label"
                                        sx={{
                                            display: 'flex', alignItems: 'center', gap: 1.25,
                                            px: 1.75, py: 1.25,
                                            border: '1.5px solid',
                                            borderColor: active ? 'var(--accent)' : 'var(--line)',
                                            bgcolor: active ? 'var(--hero)' : 'var(--paper)',
                                            borderRadius: '12px',
                                            cursor: 'pointer',
                                            transition: 'all 0.15s',
                                        }}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={active}
                                            onChange={() => toggle('rates', r.val)}
                                            style={{ accentColor: 'var(--accent)', width: 16, height: 16 }}
                                        />
                                        <Typography sx={{
                                            fontFamily: 'var(--font-display)',
                                            fontSize: 14, fontWeight: 700,
                                            color: r.val === 3 ? 'var(--accent)' : 'var(--ink)',
                                        }}>
                                            {r.emo}
                                        </Typography>
                                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>
                                            {r.label}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>

                    {/* イートイン・テイクアウト */}
                    <Box component="section" sx={{ mb: 3 }}>
                        <SecTitle n={draft.eat.length}>ご利用スタイル</SecTitle>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {EAT_DEFS.map(e => (
                                <Toggle
                                    key={e.val}
                                    active={draft.eat.includes(e.val)}
                                    onClick={() => toggle('eat', e.val)}
                                    icon={e.icon}
                                >
                                    {e.label}
                                </Toggle>
                            ))}
                        </Box>
                    </Box>

                    {/* 並べ替え */}
                    <Box component="section">
                        <SecTitle>並べ替え</SecTitle>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {[
                                { val: 'recommend', icon: '◎', label: 'おすすめ順', desc: 'おすすめ度が高いお店を上位に' },
                                { val: 'updated', icon: '🕒', label: '更新日順', desc: '最近追加・更新されたお店を上位に' },
                            ].map(s => {
                                const active = draft.sort === s.val;
                                return (
                                    <Box
                                        key={s.val}
                                        component="button"
                                        onClick={() => setDraft(d => ({ ...d, sort: s.val }))}
                                        sx={{
                                            px: 1.75, py: 1.5,
                                            border: '1.5px solid',
                                            borderColor: active ? 'var(--accent)' : 'var(--line)',
                                            bgcolor: active ? 'var(--hero)' : 'var(--paper)',
                                            borderRadius: '12px',
                                            display: 'flex', alignItems: 'center', gap: 1.25,
                                            cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
                                            transition: 'all 0.15s',
                                        }}
                                    >
                                        <span style={{ fontSize: 16 }}>{s.icon}</span>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--ink)' }}>
                                                {s.label}
                                            </Typography>
                                            <Typography sx={{ fontSize: 11, color: 'var(--muted)', mt: 0.25 }}>
                                                {s.desc}
                                            </Typography>
                                        </Box>
                                        {active && (
                                            <Typography sx={{ color: 'var(--accent)', fontWeight: 800, fontSize: 16 }}>◉</Typography>
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                </Box>

                {/* フッター */}
                <Box sx={{
                    px: isMobile ? 2 : 3, py: isMobile ? 1.5 : 1.75,
                    borderTop: '1px solid var(--line)',
                    bgcolor: 'var(--paper)',
                    display: 'flex', gap: 1.25, alignItems: 'center',
                    flexShrink: 0,
                }}>
                    <Box
                        component="button"
                        onClick={() => setDraft(INITIAL_DRAFT)}
                        sx={{
                            px: 2, py: 1.5,
                            border: '1px solid var(--line)',
                            bgcolor: 'var(--paper)', color: 'var(--ink)',
                            borderRadius: '12px', fontSize: 13, fontWeight: 600,
                            cursor: 'pointer', fontFamily: 'inherit',
                            '&:hover': { bgcolor: 'var(--surface)' },
                        }}
                    >
                        リセット
                    </Box>
                    <Box
                        component="button"
                        onClick={handleApply}
                        sx={{
                            flex: 1, py: 1.625,
                            border: 'none',
                            bgcolor: 'var(--ink)', color: 'var(--paper)',
                            borderRadius: '12px', fontSize: 14, fontWeight: 800,
                            cursor: 'pointer', fontFamily: 'inherit',
                            '&:hover': { opacity: 0.85 },
                        }}
                    >
                        {count > 0 ? `${count}件の条件で適用` : '適用'}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}

export default SearchScreen;
